import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useGetVTOResultByIdQuery } from '../redux/apis/endpoints/vtoResultApi';
import { setVirtualTryOnResult, setActivePersonImage, setCanvasIntegrationMode } from '../redux/slices/personImageSlice';
import { setActiveItems } from '../redux/slices/clothingItemsSlice';

/**
 * Hook to restore VTO result from backend on page refresh
 * Checks if there's a persisted vtoResultId and restores it
 */
export const useVTOResultRestoration = (personImageState, activePersonImageId) => {
  const dispatch = useDispatch();
  const hasRestored = useRef(false);
  const restorationAttempted = useRef(false);

  // Get persisted VTO data from localStorage
  const getPersistedVTOData = () => {
    try {
      const savedState = localStorage.getItem('vtoUIState');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return {
          vtoResultId: parsed?.currentSession?.vtoResultId || null,
          personImageId: parsed?.currentSession?.personImageId || null
        };
      }
    } catch (error) {
      console.warn('Failed to load persisted VTO data:', error);
    }
    return { vtoResultId: null, personImageId: null };
  };

  const { vtoResultId, personImageId } = getPersistedVTOData();

  console.log('[VTORestore] VTO Restoration Hook - Persisted Data:', {
    vtoResultId,
    personImageId,
    activePersonImageId,
    hasRestored: hasRestored.current,
    restorationAttempted: restorationAttempted.current
  });

  // Fetch VTO result if we have an ID
  const { data: vtoResult, isLoading, isSuccess, error } = useGetVTOResultByIdQuery(vtoResultId, {
    skip: !vtoResultId || hasRestored.current
  });

  console.log('[VTORestore] VTO Restoration Hook - Query Status:', {
    isLoading,
    isSuccess,
    hasData: !!vtoResult,
    error: error?.message,
    shouldRestore: isSuccess && vtoResult && !hasRestored.current && activePersonImageId
  });

  useEffect(() => {
    // Only restore if:
    // 1. VTO result is loaded successfully
    // 2. We haven't restored yet
    // 3. Person image state is available
    // 4. Active person image is loaded (to avoid race condition)
    if (isSuccess && vtoResult && !hasRestored.current && personImageState && activePersonImageId) {
      
      // Mark that we've attempted restoration to avoid multiple attempts
      if (restorationAttempted.current) {
        return;
      }
      restorationAttempted.current = true;

      console.log('[VTORestore] Restoring VTO result from backend:', {
        vtoResultId,
        personImageId,
        activePersonImageId,
        hasResultImage: !!(Array.isArray(vtoResult.resultImage) ? vtoResult.resultImage[0]?.url : vtoResult.resultImage?.url),
        hasPersonImage: !!vtoResult.personImage?.url,
        clothingItems: vtoResult.closetClothes?.length || 0,
        vtoResult
      });

      // Use setTimeout to ensure restoration happens after person image is fully loaded
      setTimeout(() => {
        // Restore VTO result image to person image slice
        const vtoImageUrl = Array.isArray(vtoResult.resultImage) ? vtoResult.resultImage[0]?.url : vtoResult.resultImage?.url;
        if (vtoImageUrl) {
          console.log('[VTORestore] Setting VTO result image:', vtoImageUrl);
          
          // Enable canvas integration first
          dispatch(setCanvasIntegrationMode(true));
          
          personImageState.setVirtualTryOnResult({
            image: vtoImageUrl,
            metadata: {
              isVirtualTryOnResult: true,
              vtoResultId: vtoResult.id,
              garmentType: vtoResult.garmentType,
              allClothingItemIds: vtoResult.closetClothes?.map(c => c.id) || [],
              uploadTimestamp: new Date(vtoResult.createdAt).getTime(),
              processingMethod: 'virtual-try-on',
              backendSaved: true,
              restored: true
            },
            originalImage: vtoResult.personImage?.url
          });

          // Restore clothing items to wearing list
          if (vtoResult.closetClothes && vtoResult.closetClothes.length > 0) {
            console.log('[VTORestore] Restoring clothing items to wearing list:', vtoResult.closetClothes.length);
            dispatch(setActiveItems(vtoResult.closetClothes));
          }

          hasRestored.current = true;
          console.log('[VTORestore] VTO result restored successfully', {
            image: vtoImageUrl.substring(0, 50),
            isVirtualTryOnResult: true,
            isCanvasIntegrated: true
          });
        } else {
          console.warn('[VTORestore] No result image URL found in VTO result');
        }
      }, 500); // Longer delay to ensure person image is fully loaded
    }
  }, [isSuccess, vtoResult, vtoResultId, personImageId, activePersonImageId, personImageState, dispatch]);

  return { isRestoring: isLoading, hasRestored: hasRestored.current };
};

export default useVTOResultRestoration;
